<script lang="ts" setup>
import { DefinitionDto } from '@/api/generator';
import Attachments from '@/components/forms/Attachments/Attachments.vue';
import Textarea from '@/components/forms/Textarea/Textarea.vue';
import TextField from '@/components/forms/TextField/TextField.vue';
import { EMPTY_FIELD_SIGN } from '@/components/forms/utils.ts';
import OverviewTable from '@/components/OverviewTable/OverviewTable.vue';
import { useProtocolsStore } from '@/stores/protocolsStore.ts';
import { useUnitsStore } from '@/stores/unitsStore.ts';
import { translateEntityProperty } from '@/utils/helper.ts';
import { toRangeDisplay } from '@/utils/numberUtils';
import { useTranslation } from 'i18next-vue';
import { computed, onMounted } from 'vue';

const { t } = useTranslation();

interface DefinitionContentProps {
  definition: DefinitionDto;
}

const props = defineProps<DefinitionContentProps>();

const unitsStore = useUnitsStore();
const protocolsStore = useProtocolsStore();

const unit = computed(() =>
  props.definition.unitId ? unitsStore.getById(props.definition.unitId) : undefined,
);
const protocol = computed(() =>
  props.definition.protocolId ? protocolsStore.getById(props.definition.protocolId) : undefined,
);

onMounted(async () => {
  await unitsStore.initialize();
  await protocolsStore.initialize();
});

const tableHeaders = [
  {
    title: `${t('Name')}`,
    key: 'definitions',
    sortable: false,
  },
  {
    title: `${t('Description')}`,
    key: 'description',
    sortable: false,
  },
  {
    title: `${t('ValueType')}`,
    key: 'valueType',
    sortable: false,
  },
  {
    title: `${t('Unit_one')}`,
    key: 'unit',
    sortable: false,
  },
  {
    title: `${t('DisplayedDecimals')}`,
    key: 'displayedDecimals',
    align: 'end',
    width: '50px',
    sortable: false,
  },
  {
    title: `${t('InputRange')}`,
    key: 'inputRange',
    align: 'end',
    width: '170px',
    sortable: false,
  },
] as const;
</script>

<template>
  <v-row>
    <v-col cols="5">
      <Textarea
        :modelValue="translateEntityProperty('description', definition)"
        name="description"
        mode="details"
        :label="$t('Description')"
        rows="7"
      />
      <Attachments
        class="mt-1"
        mode="details"
        :modelValue="definition.attachments"
        data-testid="Description_Attachments"
      />
    </v-col>
    <v-col cols="2">
      <TextField
        :name="$t('ValueType')"
        :label="$t('ValueType')"
        mode="details"
        :modelValue="
          $t(`${definition.valueType}`, {
            defaultValue: definition.valueType || EMPTY_FIELD_SIGN,
          })
        "
      />
    </v-col>
    <v-col cols="2">
      <TextField
        :name="$t('Unit_one')"
        :label="$t('DefinitionUnit_one')"
        :modelValue="
          $t(`${unit?.abbreviation}`, {
            defaultValue: unit?.abbreviation || EMPTY_FIELD_SIGN,
          })
        "
        mode="details"
      />
    </v-col>
    <v-col cols="2">
      <TextField
        :name="$t('Protocol_one')"
        :label="$t('DefinitionProtocol_one')"
        :modelValue="
          $t(`${protocol?.name}`, {
            defaultValue: protocol?.name || EMPTY_FIELD_SIGN,
          })
        "
        mode="details"
      />
    </v-col>
  </v-row>

  <h6 v-if="definition.definitionParameters?.length" class="text-primary-darken-2 mb-4 mt-6">
    {{
      $t('DefinitionParameter', {
        count: definition.definitionParameters.length || 0,
      })
    }}
  </h6>

  <OverviewTable
    v-if="definition.definitionParameters?.length"
    :showItemsCount="false"
    :zebra="true"
    :noSpacing="true"
    :headers="tableHeaders"
    :items="definition.definitionParameters"
    :loading="false"
    data-testid="DataTable_DefinitionParameters"
  >
    <template #item.definitions="{ item }">
      <span class="font-weight-bold">
        {{ translateEntityProperty('name', item) }}
      </span>
    </template>

    <template #item.description="{ item }">
      <template v-if="item.description">
        <p class="truncated">
          {{ translateEntityProperty('description', item) }}
        </p>
      </template>
      <template v-else>
        {{ EMPTY_FIELD_SIGN }}
      </template>
    </template>

    <template #item.valueType="{ item }">
      {{
        $t(`${item.valueType}`, {
          defaultValue: item.valueType || EMPTY_FIELD_SIGN,
        })
      }}
    </template>

    <template #item.unit="{ item }">
      <template v-if="item.unitId && unitsStore.getById(item.unitId)?.abbreviation">
        {{ unitsStore.getById(item.unitId)?.abbreviation }}
      </template>
      <template v-else>
        {{ EMPTY_FIELD_SIGN }}
      </template>
    </template>

    <template #item.displayedDecimals="{ item }">
      <template v-if="item.decimalPlaces">
        {{ item.decimalPlaces }}
      </template>
      <template v-else>
        {{ EMPTY_FIELD_SIGN }}
      </template>
    </template>

    <template #item.inputRange="{ item }">
      <template v-if="item.inputRangeMin !== undefined && item.inputRangeMax !== undefined">
        {{
          toRangeDisplay(
            item.inputRangeMin,
            item.inputRangeMax,
            item.unitId && unitsStore.getById(item.unitId)?.abbreviation
              ? unitsStore.getById(item.unitId)?.abbreviation
              : undefined,
          )
        }}
      </template>
      <template v-else>
        {{ EMPTY_FIELD_SIGN }}
      </template>
    </template>
  </OverviewTable>
</template>

<style scoped lang="scss">
:deep(.file-item) {
  // Needed to fix padding in listgroup context
  padding-inline-start: 0px !important;
}

.overview-table {
  overflow: visible;
  display: none;
}
</style>
